import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { X, Check, CalendarRange, CalendarDays, ClipboardList, ListChecks, UserCheck, FileEdit, LayoutDashboard, HeartPulse } from 'lucide-react';
export function ProfessionalComparison() {
  const comparisons = [{
    situation: 'Llegás al cierre y no recordás qué hizo cada alumno',
    without: 'Escribís de memoria y arriesgás errores',
    with: 'El Registro Diario tiene todo documentado clase por clase'
  }, {
    situation: 'Necesitás saber cuántos días faltó un alumno',
    without: 'Revisás cuaderno por cuaderno',
    with: 'La Planilla de Asistencia lo tiene calculado automáticamente'
  }, {
    situation: 'Querés saber qué alumnos están en riesgo académico',
    without: 'Lo sabés recién al cierre del período',
    with: 'El Mapa de Calor te alerta semanas antes'
  }, {
    situation: 'Tenés que planificar el año pero no sabés por dónde empezar',
    without: 'Improvisás semana a semana',
    with: 'El Planificador Anual te da la hoja de ruta completa'
  }, {
    situation: 'Necesitás escribir el informe de boletín',
    without: 'Abrís el MegaPack y buscás frases sueltas',
    with: 'El Generador de Informes te dice qué sección usar según los datos'
  }, {
    situation: 'Una familia te reclama algo y no tenés documentación',
    without: 'No tenés con qué respaldarte',
    with: 'El sistema de actas y comunicaciones tiene todo registrado'
  }];
  const modules = [{
    icon: <CalendarRange className="w-6 h-6" data-id="element-246" />,
    title: 'Planificador Anual y por Unidad Didáctica',
    color: 'text-violet-300',
    bgColor: 'bg-violet-500/20',
    items: 'Mapa del año completo · Distribución de contenidos · Mapa de evaluaciones · Metas pedagógicas y de documentación'
  }, {
    icon: <CalendarDays className="w-6 h-6" data-id="element-247" />,
    title: 'Planificador Mensual y Semanal',
    color: 'text-purple-300',
    bgColor: 'bg-purple-500/20',
    items: 'Vista semanal completa · Planificación de clase individual · Registro post-clase · Balance semanal integrado'
  }, {
    icon: <ClipboardList className="w-6 h-6" data-id="element-248" />,
    title: 'Registro Diario de Observaciones',
    color: 'text-fuchsia-300',
    bgColor: 'bg-fuchsia-500/20',
    items: 'Sistema de códigos de observación (L+, L-, C+, C-, P+, P-, TP, F, EOE) · Registro acumulado mensual · Patrones de grupo detectables automáticamente'
  }, {
    icon: <ListChecks className="w-6 h-6" data-id="element-249" />,
    title: 'Planillas Maestras de Asistencia y Calificaciones',
    color: 'text-emerald-300',
    bgColor: 'bg-emerald-500/20',
    items: 'Planilla anual con alertas de riesgo · Ficha individual de seguimiento de asistencia · Registro de evaluación por instrumento · Estadísticas del grupo'
  }, {
    icon: <UserCheck className="w-6 h-6" data-id="element-250" />,
    title: 'Ficha de Seguimiento Individual',
    color: 'text-indigo-300',
    bgColor: 'bg-indigo-500/20',
    items: 'Perfil inicial · Seguimiento bimestral · Historial de contactos con familias · Balance anual con recomendaciones'
  }, {
    icon: <FileEdit className="w-6 h-6" data-id="element-251" />,
    title: 'Generador de Informes desde el Registro',
    color: 'text-violet-300',
    bgColor: 'bg-violet-600/20',
    items: 'Proceso paso a paso para convertir registros en observaciones de boletín · Plantilla de generación con checklist de revisión final'
  }, {
    icon: <LayoutDashboard className="w-6 h-6" data-id="element-252" />,
    title: 'Dashboard Grupal y Mapa de Calor',
    color: 'text-purple-300',
    bgColor: 'bg-purple-600/20',
    items: 'Vista general del estado del grupo · Semáforo de familias · Agenda anual de reuniones · Alumnos en alerta por dimensión'
  }, {
    icon: <HeartPulse className="w-6 h-6" data-id="element-253" />,
    title: 'Monitor de Bienestar Docente',
    color: 'text-rose-300',
    bgColor: 'bg-rose-500/20',
    items: 'Checklist de autocuidado semanal · Señales de alerta de agotamiento · Plan de autocuidado mensual'
  }];
  return <section className="py-20 bg-black" data-id="element-254">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-255">
        <div className="text-center mb-12" data-id="element-256">
          <h2 className="md:text-4xl font-extrabold text-white mb-4 text-[20px]" data-id="element-257">
            🎯 ¿QUÉ AGREGA EL "PLANIFICADOR SISTÉMICO"?
          </h2>
          <p className="text-zinc-200 text-[14px]" data-id="element-258">
            La diferencia entre tener las frases y tener el sistema completo que
            las genera.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="mb-16 overflow-x-auto pb-4" data-id="element-259">
          <div className="min-w-[800px]" data-id="element-260">
            <div className="grid grid-cols-3 gap-4 mb-4 px-4" data-id="element-261">
              <div className="font-bold text-violet-300 uppercase text-sm tracking-wider" data-id="element-262">
                Situación real
              </div>
              <div className="font-bold text-red-400 uppercase text-sm tracking-wider flex items-center gap-2" data-id="element-263">
                <X className="w-4 h-4" data-id="element-264" /> Sin Planificador
              </div>
              <div className="font-bold text-emerald-400 uppercase text-sm tracking-wider flex items-center gap-2" data-id="element-265">
                <Check className="w-4 h-4" data-id="element-266" /> Con Planificador
              </div>
            </div>

            <div className="space-y-3" data-id="element-267">
              {comparisons.map((row, i) => <div key={i} className="grid grid-cols-3 gap-4 bg-zinc-900/60 rounded-xl p-4 items-center border border-violet-900/30" data-id="element-268">
                  <div className="font-medium text-white" data-id="element-269">{row.situation}</div>
                  <div className="text-zinc-300 text-sm" data-id="element-270">{row.without}</div>
                  <div className="text-emerald-200 font-medium text-sm bg-emerald-500/15 p-2 rounded-lg border border-emerald-500/30" data-id="element-271">
                    {row.with}
                  </div>
                </div>)}
            </div>
          </div>
          <p className="text-center text-sm text-violet-400 mt-4 md:hidden" data-id="element-272">
            ← Scroll to see full table →
          </p>
        </div>

        {/* Modules Grid */}
        <div className="bg-zinc-900 rounded-3xl p-8 md:p-12 text-white shadow-xl border border-violet-900/40" data-id="element-273">
          <h3 className="text-2xl font-bold mb-8 flex items-center gap-3 text-white" data-id="element-274">
            <span className="bg-emerald-500 text-white p-1 rounded-full" data-id="element-275">
              <Check className="w-5 h-5" data-id="element-276" />
            </span>
            EL PLANIFICADOR SISTÉMICO INCLUYE:
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-277">
            {modules.map((mod, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="bg-black/50 rounded-2xl p-6 border border-violet-900/30" data-id="element-278">
                <div className="flex items-center gap-3 mb-4" data-id="element-279">
                  <div className={`p-2 rounded-lg ${mod.bgColor} ${mod.color}`} data-id="element-280">
                    {mod.icon}
                  </div>
                  <h4 className="font-bold text-lg text-white" data-id="element-281">{mod.title}</h4>
                </div>
                <p className="text-zinc-200 text-sm leading-relaxed" data-id="element-282">
                  {mod.items}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}